import type { Payout } from '../types/payout';
import * as XLSX from 'xlsx';
import { Sentry } from './sentry';

// Format Waltio selon le template officiel
export interface WaltioRecord {
  Type: string;
  Date: string;
  'Received amount': string;
  'Currency or token received': string;
  'Sent amount': string;
  'Currency or token sent': string;
  'Fee': string;
  'Currency or token fee': string;
  'Exchange / Platform': string;
  'Description': string;
  'Label': string;
}

export const formatPayoutForWaltio = (payout: Payout): WaltioRecord => {
  const formattedDate = new Date(payout.date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).replace(/(\d{2})\/(\d{2})\/(\d{4}), (\d{2}):(\d{2})/, '$1/$2/$3 $4:$5');

  // Handle swap transactions
  if (payout.type === 'swap') {
    return {
      Type: 'Trade',
      Date: formattedDate,
      'Received amount': payout.receivedAmount?.toString() || '',
      'Currency or token received': payout.receivedCrypto?.symbol.toUpperCase() || '',
      'Sent amount': payout.sentAmount?.toString() || '',
      'Currency or token sent': payout.sentCrypto?.symbol.toUpperCase() || '',
      'Fee': payout.fees?.toString() || '',
      'Currency or token fee': payout.feesCurrency || 'EUR',
      'Exchange / Platform': payout.platform || 'SWAP',
      'Description': `Swap ${payout.sentCrypto?.name} → ${payout.receivedCrypto?.name}`,
      'Label': 'Trade'
    };
  }

  // Handle sale transactions
  if (payout.type === 'vente') {
    return {
      Type: 'Withdrawal',
      Date: formattedDate,
      'Received amount': '',
      'Currency or token received': '',
      'Sent amount': payout.amount?.toString() || '',
      'Currency or token sent': payout.crypto?.symbol.toUpperCase() || '',
      'Fee': '',
      'Currency or token fee': '',
      'Exchange / Platform': 'SALE',
      'Description': `Sale of ${payout.crypto?.name}`,
      'Label': 'Sell'
    };
  }

  // Handle mining and staking (original logic)
  const isStaking = payout.type === 'stacking';
  const label = isStaking ? 'Masternodes & staking' : 'Mining';
  const platform = isStaking ? 'STAKING' : 'MINING';
  const description = isStaking
    ? `Staking reward - ${payout.crypto?.name}`
    : `Mining payout - ${payout.crypto?.name}`;

  return {
    Type: 'Deposit',
    Date: formattedDate,
    'Received amount': payout.amount?.toString() || '',
    'Currency or token received': payout.crypto?.symbol.toUpperCase() || '',
    'Sent amount': '',
    'Currency or token sent': '',
    'Fee': '',
    'Currency or token fee': '',
    'Exchange / Platform': platform,
    'Description': description,
    'Label': label
  };
};

export const exportWaltioCSV = (payouts: Payout[], filename: string): void => {
  // Filter out "achat" transactions - include payout, stacking, vente, and swap types
  const payoutTypeOnly = payouts.filter(p => p.type !== 'achat');

  if (payoutTypeOnly.length === 0) {
    alert('Aucun payout à exporter');
    return;
  }

  const headers = [
    'Type',
    'Date',
    'Received amount',
    'Currency or token received',
    'Sent amount',
    'Currency or token sent',
    'Fee',
    'Currency or token fee',
    'Exchange / Platform',
    'Description',
    'Label'
  ];

  const waltioRecords = payoutTypeOnly.map(formatPayoutForWaltio);

  const csvContent = [
    headers.join(','),
    ...waltioRecords.map(record => [
      `"${record.Type}"`,
      `"${record.Date}"`,
      record['Received amount'],
      `"${record['Currency or token received']}"`,
      record['Sent amount'],
      `"${record['Currency or token sent']}"`,
      record.Fee,
      `"${record['Currency or token fee']}"`,
      `"${record['Exchange / Platform']}"`,
      `"${record.Description}"`,
      `"${record.Label}"`
    ].join(','))
  ].join('\n');

  // Ajouter le BOM UTF-8 pour Excel
  const csvContentWithBOM = '\uFEFF' + csvContent;

  const blob = new Blob([csvContentWithBOM], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const exportWaltioXLSX = async (payouts: Payout[], filename: string): Promise<void> => {
  // Filter out "achat" transactions - only include "payout" and "stacking" types
  const payoutTypeOnly = payouts.filter(p => p.type === 'payout' || p.type === 'stacking' || !p.type);

  if (payoutTypeOnly.length === 0) {
    alert('Aucun payout à exporter');
    return;
  }

  try {
    // Charger le template Waltio
    const templateUrl = '/Waltio-Template-en.xlsx';
    const templateResponse = await fetch(templateUrl);

    // Vérifier que le template a bien été trouvé
    if (!templateResponse.ok) {
      throw new Error(`Template non trouvé (${templateResponse.status}): ${templateUrl}`);
    }

    // Vérifier le Content-Type pour s'assurer que ce n'est pas du HTML
    const contentType = templateResponse.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error('Le template retourne du HTML au lieu d\'un fichier Excel. Vérifiez que le fichier existe dans le dossier public.');
    }

    const templateBuffer = await templateResponse.arrayBuffer();

    // Lire le template
    const templateWorkbook = XLSX.read(templateBuffer, { type: 'array' });
    const worksheet = templateWorkbook.Sheets['Your data'];

    // Convertir les payouts au format Waltio
    const waltioRecords = payoutTypeOnly.map(formatPayoutForWaltio);
    
    // Ajouter les données à partir de la ligne 2 (ligne 1 = headers)
    waltioRecords.forEach((record, index) => {
      const row = index + 2; // Commencer à la ligne 2
      
      worksheet[`A${row}`] = { t: 's', v: record.Type };
      worksheet[`B${row}`] = { t: 's', v: record.Date };
      worksheet[`C${row}`] = { t: 'n', v: parseFloat(record['Received amount']) };
      worksheet[`D${row}`] = { t: 's', v: record['Currency or token received'] };
      worksheet[`E${row}`] = { t: 's', v: record['Sent amount'] };
      worksheet[`F${row}`] = { t: 's', v: record['Currency or token sent'] };
      worksheet[`G${row}`] = { t: 's', v: record.Fee };
      worksheet[`H${row}`] = { t: 's', v: record['Currency or token fee'] };
      worksheet[`I${row}`] = { t: 's', v: record['Exchange / Platform'] };
      worksheet[`J${row}`] = { t: 's', v: record.Description };
      worksheet[`K${row}`] = { t: 's', v: record.Label };
    });
    
    // Mettre à jour la plage du worksheet
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    range.e.r = Math.max(range.e.r, waltioRecords.length + 1);
    worksheet['!ref'] = XLSX.utils.encode_range(range);
    
    // Générer le fichier Excel
    const excelBuffer = XLSX.write(templateWorkbook, { bookType: 'xlsx', type: 'array' });
    
    // Télécharger le fichier
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error('Erreur lors de l\'export Excel:', error);
    Sentry.captureException(error, {
      tags: { operation: 'exportWaltioXLSX' },
      extra: { filename, payoutCount: payouts.length }
    });
    alert('Erreur lors de la génération du fichier Excel. Utilisation du format CSV.');
    // Fallback vers CSV en cas d'erreur
    exportWaltioCSV(payouts, filename.replace('.xlsx', '.csv'));
  }
};

export const exportWaltioCurrentSession = async (payouts: Payout[]): Promise<void> => {
  // Filter out "achat" transactions first
  const payoutTypeOnly = payouts.filter(p => p.type === 'payout' || p.type === 'stacking' || !p.type);

  // Filtrer les payouts de la session en cours (période de déclaration actuelle)
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Session en cours = mois en cours
  const sessionPayouts = payoutTypeOnly.filter(payout => {
    const payoutDate = new Date(payout.date);
    return payoutDate.getFullYear() === currentYear &&
           payoutDate.getMonth() === currentMonth;
  });

  const filename = `waltio_session_${currentYear}_${(currentMonth + 1).toString().padStart(2, '0')}.xlsx`;
  await exportWaltioXLSX(sessionPayouts, filename);
};

export const exportWaltioCurrentYear = async (payouts: Payout[]): Promise<void> => {
  // Filter out "achat" transactions first
  const payoutTypeOnly = payouts.filter(p => p.type === 'payout' || p.type === 'stacking' || !p.type);

  // Filtrer les payouts de l'année en cours
  const currentYear = new Date().getFullYear();

  const yearPayouts = payoutTypeOnly.filter(payout => {
    const payoutDate = new Date(payout.date);
    return payoutDate.getFullYear() === currentYear;
  });

  const filename = `waltio_annee_${currentYear}.xlsx`;
  await exportWaltioXLSX(yearPayouts, filename);
};